/**
 * Bulk Assign Role(s) dialogs for the User Manager list (ORCH-388)
 *
 * Purpose: `BulkAssignRolesDialog` is the picker and the confirmation in one: the shared
 *          RoleSelect (only roles the admin may assign) and a summary of what Assign will do.
 *          `BulkRolesResultDialog` lists who was updated and who was skipped, with the reason,
 *          when the server skipped anyone. Bulk Resend / Cancel Invite reuse it (ORCH-420),
 *          with a title of their own and the emails that didn't go out.
 * Role: opened from the list's action bar; the server checks each person on their own.
 * Dependencies: RoleSelect, LoadingButton, SecondaryButton
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { LoadingButton, RoleSelect, SecondaryButton } from '../../../components/common';
import { Role } from '../../../types/authz';
import { BulkInviteResult, BulkRolesResult, BulkRolesSkip } from '../../../types/userManagement';

const users = (count: number) => `${count} ${count === 1 ? 'user' : 'users'}`;
const rolesWord = (count: number) => `${count} ${count === 1 ? 'role' : 'roles'}`;

const SKIP_REASONS: Record<string, string> = {
  OUT_OF_SCOPE: 'outside the people you manage',
  SELF_ASSIGNMENT: "you can't change your own roles",
  NOT_INVITED: 'already joined',
  NOT_FOUND: 'no longer in this tenant',
};

const EMAIL_REASONS: Record<string, string> = {
  failed: 'the mail server did not accept it',
  not_configured: "email isn't set up in this environment",
  not_allowed: "this environment doesn't email that address",
};

const ROLE_REASONS: Record<string, (role: string) => string> = {
  ESCALATION: (role) => `${role} would give access wider than yours`,
  SYSTEM_ROLE_NOT_HELD: (role) => `${role} is a system role you don't hold`,
};

/** Why one person was skipped, as a sentence: "Tenant Admin would give access wider than yours". */
export const roleSkipReason = (skip: BulkRolesSkip, roles: Role[]): string => {
  if (skip.reason !== 'ROLE_NOT_ASSIGNABLE' || !skip.roles) {
    return SKIP_REASONS[skip.reason] || skip.reason;
  }
  return Object.entries(skip.roles)
    .map(([roleId, code]) => {
      const name = roles.find((r) => r.role_id === roleId)?.name || 'a role';
      return (ROLE_REASONS[code] || ((role: string) => `you can't assign ${role}`))(name);
    })
    .join('; ');
};

/** "9 updated, 1 already had these roles, 2 skipped." */
export const bulkRolesSummary = (result: BulkRolesResult): string =>
  [
    `${result.changed} updated`,
    ...(result.unchanged ? [`${result.unchanged} already had these roles`] : []),
    ...(result.skipped.length ? [`${result.skipped.length} skipped`] : []),
  ].join(', ') + '.';

/** The notice when nobody was skipped: "11 users updated." */
export const bulkRolesNotice = (result: BulkRolesResult): string =>
  `${users(result.changed)} updated.` +
  (result.unchanged ? ` ${result.unchanged} already had these roles.` : '');

export interface BulkAssignRolesDialogProps {
  open: boolean;
  /** The role catalog; only roles marked assignable are offered. */
  roles: Role[];
  /** How many people the selection holds. */
  count: number;
  /** The admin's own row is in the selection. */
  includesSelf: boolean;
  working: boolean;
  onAssign: (roleIds: string[]) => void;
  onClose: () => void;
}

export const BulkAssignRolesDialog: React.FC<BulkAssignRolesDialogProps> = ({
  open,
  roles,
  count,
  includesSelf,
  working,
  onAssign,
  onClose,
}) => {
  const [picked, setPicked] = useState<string[]>([]);

  useEffect(() => {
    if (open) setPicked([]);
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={working ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      data-testid="bulk-roles-dialog"
    >
      <DialogTitle>Assign Role(s)</DialogTitle>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}
      >
        <RoleSelect
          roles={roles}
          value={picked}
          onChange={setPicked}
          label="Role(s)"
          dataTestId="bulk-roles-select"
        />
        <Typography variant="body2" data-testid="bulk-roles-summary">
          {picked.length
            ? `Assign ${rolesWord(picked.length)} to ${users(count)}. Roles they already hold are kept.`
            : `Pick the roles to add to ${users(count)}. Roles they already hold are kept.`}
          {includesSelf ? " Your own roles won't change." : ''}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <SecondaryButton onClick={onClose} disabled={working} data-testid="bulk-roles-cancel">
          Cancel
        </SecondaryButton>
        <LoadingButton
          variant="contained"
          loading={working}
          disabled={!picked.length}
          onClick={() => onAssign(picked)}
          data-testid="bulk-roles-assign"
        >
          Assign
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export interface BulkRolesResultDialogProps {
  result: BulkRolesResult | BulkInviteResult | null;
  roles: Role[];
  onClose: () => void;
  /** The dialog's title; "Roles Assigned" unless the result is a bulk invite action. */
  title?: string;
}

const emailFailures = (result: BulkRolesResult | BulkInviteResult) =>
  'email_failed' in result ? result.email_failed : [];

export const BulkRolesResultDialog: React.FC<BulkRolesResultDialogProps> = ({
  result,
  roles,
  onClose,
  title = 'Roles Assigned',
}) => (
  <Dialog open={!!result} onClose={onClose} maxWidth="sm" fullWidth data-testid="bulk-roles-result">
    <DialogTitle>{title}</DialogTitle>
    <DialogContent>
      {result && (
        <>
          <Typography variant="body2" data-testid="bulk-roles-result-summary">
            {bulkRolesSummary(result)}
          </Typography>
          {result.updated.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2">Updated</Typography>
              <List dense disablePadding data-testid="bulk-roles-updated">
                {result.updated.map((person) => (
                  <ListItem key={person.user_id} disableGutters>
                    <ListItemText primary={person.name || 'Unknown user'} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
          {emailFailures(result).length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2">Email not sent</Typography>
              <List dense disablePadding data-testid="bulk-invite-email-failed">
                {emailFailures(result).map((failure) => (
                  <ListItem key={failure.user_id} disableGutters>
                    <ListItemText
                      primary={failure.name || 'Unknown user'}
                      secondary={
                        failure.email_error ||
                        EMAIL_REASONS[failure.email_status] ||
                        failure.email_status
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
          {result.skipped.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2">Skipped</Typography>
              <List dense disablePadding data-testid="bulk-roles-skipped">
                {result.skipped.map((skip) => (
                  <ListItem key={skip.user_id} disableGutters>
                    <ListItemText
                      primary={skip.name || 'Unknown user'}
                      secondary={roleSkipReason(skip, roles)}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </>
      )}
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <SecondaryButton onClick={onClose} data-testid="bulk-roles-result-close">
        Close
      </SecondaryButton>
    </DialogActions>
  </Dialog>
);
